import { presencePlace } from '@worker/policy-builder/client'
import type { StudioVisitor } from '@worker/policy-builder/client'

/**
 * One entry per person, never `self`: someone with three tabs open shows as
 * one avatar, and your own other tabs are not "someone else". A tab that has
 * not said who it is (`user`) counts as its own person.
 */
export const people = <P extends { id: string; user?: string }>(
  peers: P[],
  self: string | undefined,
): P[] => [
  ...new Map(
    peers
      .filter((peer) => !self || peer.user !== self)
      .map((peer) => [peer.user ?? peer.id, peer] as const),
  ).values(),
]

/**
 * Who has been here but is not here now, most recent first: neither you nor
 * anyone with a live tab on this place, since they already show in colour.
 */
export const pastVisitors = (
  visitors: StudioVisitor[],
  live: { user?: string }[],
  self: string | undefined,
): StudioVisitor[] => {
  const here = new Set(live.map((peer) => peer.user))
  return visitors.filter(
    (visit) => !visit.user || (visit.user !== self && !here.has(visit.user)),
  )
}

/** Whether a route is anywhere in the Policy Studio, queues and policies alike. */
export const isInStudioPath = (path: string): boolean =>
  path === '/policy-studio' || path.startsWith('/policy-studio/')

/** Every other tab on `place` (a queue or a policy, from `presencePlace`). */
export const peersAtPlace = <P extends { path?: string }>(
  peers: readonly P[],
  place: string,
): P[] =>
  peers.filter(
    (peer) => peer.path !== undefined && presencePlace(peer.path) === place,
  )

/** Who else is on `itemId`, filtered before de-duplicating so a person's tab
 *  on it wins over their other tabs. */
export const peersFocusedOn = <
  P extends { id: string; user?: string; focus?: string },
>(
  peers: readonly P[],
  itemId: string,
  self: string | undefined,
): P[] => people(peers.filter((peer) => peer.focus === itemId), self)
